import { useMemo, useCallback, Fragment } from "react";
import useWalletTransactionList from "../../../Hooks/storageRoles/useWalletTransactionList";
import {
  PageShell,
  PageHeader,
  ListSkeleton,
  EmptyState,
  ListView,
  ListToolbar,
} from "../../../Components/shared";
import SearchDateFilters from "../../../Components/wallet/SearchDateFilters";
import { Pagination } from "../../../Components/atoms";
import { IoWalletOutline } from "react-icons/io5";
import {
  FaLayerGroup,
  FaCheckCircle,
  FaTimesCircle,
  FaClock,
} from "react-icons/fa";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import trLocale from "date-fns/locale/tr";
import {
  UserCell,
  StatusBadge,
  AmountText,
  formatDate,
} from "../../../Components/wallet/walletTxUi";

const FILTERS = [
  { id: "all", label: "Tümü", icon: FaLayerGroup },
  { id: "success", label: "Başarılı", icon: FaCheckCircle },
  { id: "pending", label: "Bekleyen", icon: FaClock },
  { id: "failed", label: "Başarısız", icon: FaTimesCircle },
];

const TABLE_COLUMNS = [
  { label: "Sipariş No", width: "w-40", skeletonWidth: "w-28" },
  { label: "Kullanıcı", width: "", skeletonWidth: "w-32", skeletonExtra: true },
  { label: "Tutar", width: "w-28", skeletonWidth: "w-16" },
  { label: "Hediye Okkalira", width: "w-32", skeletonWidth: "w-16" },
  { label: "Ödeme Yöntemi", width: "w-36 whitespace-nowrap", skeletonWidth: "w-14" },
  { label: "Durum", width: "w-28", skeletonWidth: "w-16" },
  { label: "Tarih", width: "w-40", skeletonWidth: "w-24" },
];

// Raporlardaki DatePicker görünümüyle aynı stil


// `embedded`: PaymentsPage sekmesi olarak render edilirken kendi PageShell'ini
// KURMAZ (kabuk ust sayfada). Kendi PageHeader/stat blogu sekme icinde kalir.
// Durum sekmeleri sunucuda süzülür (#242)
const toParams = (filter) => ({ type: "topup", status: filter === "all" ? undefined : filter });

function WalletTopupPage({ embedded = false }) {
  const {
    transactions: topups,
    total,
    totalPages,
    counts,
    isLoading,
    isFetching,
    activeFilter,
    setActiveFilter,
    search,
    setSearch,
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    currentPage,
    setCurrentPage,
    pageSize,
    clear,
  } = useWalletTransactionList({ toParams });

  const handlePageChange = useCallback(
    (page) => {
      setCurrentPage(page);
      const tableContainer = document.querySelector(".overflow-y-auto");
      if (tableContainer) tableContainer.scrollTop = 0;
    },
    [setCurrentPage]
  );

  // Sayaçlar sunucudan: arama + tarih uygulanmış, durum sekmesinden bağımsız.
  // Eski sunucu `counts` göndermez → 0 yerine hiç gösterme
  const stats = useMemo(
    () => counts && [
      { icon: FaLayerGroup, value: counts?.all ?? 0, label: "Toplam", color: "blue" },
      { icon: FaCheckCircle, value: counts?.success ?? 0, label: "Başarılı", color: "green" },
      { icon: FaClock, value: counts?.pending ?? 0, label: "Bekleyen", color: "amber" },
      { icon: FaTimesCircle, value: counts?.failed ?? 0, label: "Başarısız", color: "indigo" },
    ],
    [counts]
  );

  // Tarih aralığı — raporlardaki tarih seçicilerle aynı görünüm.
  // ListToolbar `search` node'u olarak verilir: masaüstünde arama satırında,
  // mobilde filtre modalının içinde görünür.
  const dateRangeNode = (
    <SearchDateFilters
      search={search}
      onSearchChange={setSearch}
      placeholder="Sipariş No, kullanıcı"
      startDate={startDate}
      setStartDate={setStartDate}
      endDate={endDate}
      setEndDate={setEndDate}
    />
  );

  const Shell = embedded ? Fragment : PageShell;

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={trLocale}>
      <Shell>
        {/* Sekme olarak gomuluyken baslik ustte (PaymentsPage) tek sefer
            durur; burada ayri bir baslik satiri olmaz ve istatistik rozetleri
            asagida filtre butonlariyla AYNI satira gecer. */}
        {!embedded && (
          <div className="px-3 py-2.5 sm:p-4 md:p-6 bg-gray-50/80 sm:bg-gray-50 shrink-0">
            <PageHeader
              icon={IoWalletOutline}
              iconColor="emerald"
              title="Bakiye Yükleme"
              subtitle="Cüzdan bakiye yükleme işlemleri"
              stats={stats}
              isLoading={isLoading}
              mobileSlotId="wallet-toolbar-mobile-slot"
            />
          </div>
        )}

        <div className="bg-white flex-1 min-h-0 flex flex-col">
          <ListToolbar
            className={
              embedded
                ? "px-3 pt-2.5 pb-2.5 sm:px-4 sm:pt-4 sm:pb-4 md:px-6 md:pt-4 md:pb-6"
                : "px-3 pb-2.5 sm:px-4 sm:pb-4 md:px-6 md:pb-6"
            }
            mobileFilterSlotId="wallet-toolbar-mobile-slot"
            stats={embedded ? stats : undefined}
            filterTabs={{
              filters: FILTERS,
              activeFilter,
              onChange: setActiveFilter,
              totalCount: total,
              isLoading: isFetching,
              color: "emerald",
            }}
            search={dateRangeNode}
            onClear={clear}
          />

          <ListView
            headers={TABLE_COLUMNS.map((c) => ({ label: c.label, className: c.width }))}
            data={topups}
            rowKey="_id"
            renderRow={(tx) => (
              <tr key={tx._id} className="bg-white hover:bg-gray-50 transition-colors">
                <td className="px-4 py-3 text-sm text-gray-700 font-mono">
                  {tx.reference || "-"}
                </td>
                <td className="px-4 py-3">
                  <UserCell user={tx.user} />
                </td>
                <td className="px-4 py-3 text-sm">
                  <AmountText tx={tx} />
                </td>
                <td className="px-4 py-3 text-sm text-gray-700">
                  {tx.giftOkkalira > 0 ? tx.giftOkkalira : "-"}
                </td>
                <td className="px-4 py-3 text-sm text-gray-500 uppercase">
                  {tx.gateway || "-"}
                </td>
                <td className="px-4 py-3">
                  <StatusBadge status={tx.status} />
                </td>
                <td className="px-4 py-3 text-sm text-gray-500">
                  {formatDate(tx.date)}
                </td>
              </tr>
            )}
            renderMobileCard={(tx) => (
              <div className="bg-white px-3.5 py-3 border-b border-gray-200">
                <div className="flex items-center justify-between mb-1.5">
                  <AmountText tx={tx} className="text-sm" />
                  <StatusBadge status={tx.status} />
                </div>
                <UserCell user={tx.user} compact />
                <div className="flex items-center justify-between mt-1.5 text-xs text-gray-400">
                  <span className="font-mono truncate">{tx.reference || "-"}</span>
                  <span>{formatDate(tx.date)}</span>
                </div>
                {tx.giftOkkalira > 0 && (
                  <div className="text-xs text-yellow-600 mt-1">
                    +{tx.giftOkkalira} Okkalira hediye
                  </div>
                )}
              </div>
            )}
            isLoading={isFetching}
            loadingNode={
              <ListSkeleton columns={TABLE_COLUMNS} rowCount={10} mobileCardCount={6} />
            }
            empty={
              <div className="p-4 md:p-6">
                <EmptyState
                  message="Bakiye yükleme kaydı bulunamadı"
                  suggestion="Filtreleri değiştirin veya daha sonra tekrar deneyin."
                />
              </div>
            }
            footer={
              totalPages > 1 ? (
                <div className="shrink-0 border-t border-gray-200 bg-white">
                  <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={handlePageChange}
                    itemsPerPage={pageSize}
                    totalItems={total}
                    showInfo={true}
                    disabled={isFetching}
                  />
                </div>
              ) : null
            }
          />
        </div>
      </Shell>
    </LocalizationProvider>
  );
}

export default WalletTopupPage;
